import { Loader2 } from "lucide-react";
import { useAuth } from "@/components/providers/auth-provider";
import { useUsage, type UsageByKind } from "@/hooks/use-usage";
import { ErrorState } from "@/components/ui/error-state";

const KIND_LABELS: Record<string, string> = {
  enrichment: "Enriquecimiento (ingesta)",
  chat: "Chat",
  embedding: "Embeddings",
  rerank: "Rerank",
};

const KIND_PROVIDER: Record<string, string> = {
  enrichment: "Anthropic (Claude)",
  chat: "Anthropic (Claude)",
  embedding: "Voyage",
  rerank: "Voyage",
};

function fmt(n: number): string {
  return n.toLocaleString("es-ES");
}

export default function UsoPage() {
  const { isAdmin } = useAuth();
  const usageQuery = useUsage();

  if (!isAdmin) {
    return (
      <div className="max-w-container mx-auto px-4 py-6">
        <ErrorState message="Solo los administradores pueden ver el consumo." />
      </div>
    );
  }

  const rows: UsageByKind[] = usageQuery.data ?? [];
  const totalIn = rows.reduce((a, r) => a + r.input_tokens, 0);
  const totalOut = rows.reduce((a, r) => a + r.output_tokens, 0);

  return (
    <div className="max-w-container mx-auto px-4 lg:px-6 py-6 space-y-4">
      <div>
        <h1 className="text-2xl font-heading font-semibold text-ink">Consumo de IA</h1>
        <p className="text-sm text-content-muted mt-0.5 max-w-2xl">
          Tokens acumulados por tipo de llamada. El <strong>enriquecimiento</strong> (ingesta)
          y el <strong>chat</strong> consumen Anthropic; los <strong>embeddings</strong> y el{" "}
          <strong>rerank</strong>, Voyage.
        </p>
      </div>

      {usageQuery.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 text-content-muted animate-spin" />
        </div>
      ) : usageQuery.isError ? (
        <ErrorState
          message={usageQuery.error?.message ?? "Error cargando el consumo."}
          onRetry={() => usageQuery.refetch()}
        />
      ) : rows.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-line-strong rounded-md">
          <p className="text-sm text-content">Aún no hay consumo registrado.</p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-line">
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Tipo</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Proveedor</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Llamadas</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Tokens entrada</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Tokens salida</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden md:table-cell">Caché (lectura)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {rows.map((r) => (
                  <tr key={r.kind} className="hover:bg-surface-100">
                    <td className="px-4 py-2.5 text-sm font-medium text-ink">{KIND_LABELS[r.kind] ?? r.kind}</td>
                    <td className="px-4 py-2.5 text-sm text-content">{KIND_PROVIDER[r.kind] ?? "—"}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums">{fmt(r.calls)}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums">{fmt(r.input_tokens)}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums">{fmt(r.output_tokens)}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums hidden md:table-cell">{fmt(r.cache_read_tokens)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-100 border-t border-line font-medium">
                  <td className="px-4 py-2.5 text-sm text-ink" colSpan={3}>Total</td>
                  <td className="px-4 py-2.5 text-sm text-ink text-right tabular-nums">{fmt(totalIn)}</td>
                  <td className="px-4 py-2.5 text-sm text-ink text-right tabular-nums">{fmt(totalOut)}</td>
                  <td className="px-4 py-2.5 hidden md:table-cell" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
